import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Box, Typography, IconButton, Tooltip, CircularProgress } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import UndoIcon from '@mui/icons-material/Undo'
import RedoIcon from '@mui/icons-material/Redo'
import { FS_XS, ICON_XS, FS_SM, BORDER_BLACK } from '../common/theme'
import { copyToClipboard } from '../common/clipboardHelpers'
import OpenInAdvancedEditButton from './buttons/OpenInAdvancedEditButton'
import TaResponse, { structureToPlainText, type TaResponseHandle } from './TaResponse'
import TaResponseGutterColumn from './TaResponseGutterColumn'
import TaPadlock from './TaPadlock'
import TaWordCount from './TaWordCount'
import TaThreeDots from './TaThreeDots'
import TaElements from './TaElements'
import TaTraceToggle from './TaTraceToggle'
import TraceArc from './TraceArc'
import ScoreArc from './ScoreArc'
import type { Question, TextStructure } from '../types'
import { useAppActions } from '../AppContext'
import { AnswerWebResult } from '../types/answerwebresult'
import { hasAnswerText } from '../common/common'



// Shallow-equality check for the sentence-position map — used by the
// measurement effect to skip state updates (and the ripple re-renders
// they'd cause) when a fresh measurement matches what we already had.
function sameMap(
  a: Map<string, { top: number; lineHeight: number }>,
  b: Map<string, { top: number; lineHeight: number }>,
): boolean {
  if (a.size !== b.size) return false
  for (const [k, v] of b) {
    const p = a.get(k)
    if (!p || p.top !== v.top || p.lineHeight !== v.lineHeight) return false
  }
  return true
}

export interface BidQuestionAnswerTabProps {
  question: Question
  // Open the word-wizard for this answer (only used when the parent wires it).
  onOpenWordWizard?: () => void

  // Streaming / running flags, used to swap icons for spinners and to disable
  // the editor while the answer is being generated.
  isStreaming?: boolean
  tracing?: boolean
  scoring?: boolean

  // Lock state for the answer.
  isLocked?: boolean
  onToggleLock?: () => void

  // Optional secondary actions (variant-specific bits that don't belong on
  // the inner component).
  threeDotsItems?: { label: string; icon?: any; onClick: () => void; danger?: boolean; dividerAbove?: boolean }[]

  // Slot rendered at the right end of the answer header (used by RBT for the
  // Regen Answer button).
  headerEndSlot?: ReactNode

  // The id passed to TaQueryBar - must be unique per editor on the page.
  editorId?: string
}

// Self-contained "Your Answer" panel - the right-hand half of a question
// editor. Renders the answer header (title + trace/score arcs), a
// toolbar (tweaks + "AI can make mistakes" hint + undo/redo/copy/edit),
// and the editable answer content with an optional sim gutter beside
// it. Used by BidQuestion (inline list view) and ResponseBuilderTab.
export default function BidQuestionAnswerTab({
  question, onOpenWordWizard,
  isStreaming = false, tracing = false, scoring = false,
  isLocked = false, onToggleLock,
  threeDotsItems,
  headerEndSlot,
  editorId,
}: BidQuestionAnswerTabProps) {

  const { updateQuestion } = useAppActions()
  const hasAnswer = hasAnswerText(question)
  // Trace gutter visibility — defaults on. Its toggle lives on the
  // toolbar row.
  const [showTrace, setShowTrace] = useState(true)
  // Undo / redo for the answer editor - TaResponse owns the history
  // buffer; we just expose buttons that drive its imperative handle.
  const taResponseRef = useRef<TaResponseHandle>(null)
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)
  // Shared hover state between the sim gutter column and the editor
  // beside it. The gutter sets this on mouse-enter of a block and
  // BasicView highlights the matching `data-sentence-key` span via a
  // CSS attribute selector — see TaResponse's BasicView.
  const [hoveredSentence, setHoveredSentence] = useState<{ key: string; color?: string } | null>(null)
  // Ref to the container wrapping the editable text — used below to
  // measure where each sentence's first visible line sits so the
  // gutter can place a fixed-size block anchored to that line.
  const textContainerRef = useRef<HTMLDivElement>(null)
  const [sentencePositions, setSentencePositions] = useState<Map<string, { top: number; lineHeight: number }>>(new Map())

  // Cache measurements keyed by (container width) so a
  // ResizeObserver tick that reports a width we've already measured
  // is a no-op. Prevents an oscillation loop the gutter used to
  // trigger — text width change → new positions → gutter rebalance
  // → text width change back → new measurement → …
  const measurementCacheRef = useRef<Map<number, Map<string, { top: number; lineHeight: number }>>>(new Map())

  // Circuit breaker — if the ResizeObserver → measure → setState →
  // reflow loop starts thrashing (e.g. gutter width and text width
  // ping-pong faster than the width cache can absorb), trip after
  // `CB_MAX_PER_WINDOW` measurements inside a `CB_WINDOW_MS` window
  // and freeze the gutter for `CB_COOLDOWN_MS` so the layout can
  // settle. Numbers picked to be well above any legitimate drag
  // cadence but low enough to catch a real runaway within a couple
  // of frames.
  const CB_MAX_PER_WINDOW = 40
  const CB_WINDOW_MS = 500
  const CB_COOLDOWN_MS = 400
  const circuitRef = useRef({ windowStart: 0, count: 0, cooldownUntil: 0 })

  // Measure the first client-rect of every `[data-sentence-key]`
  // span. `getClientRects()[0]` returns the rectangle for the first
  // visible line of that inline element, so its `top` is the line's
  // y-coord and `height` is the line-height in px. We translate top
  // into container-relative coords so the gutter (a sibling) can
  // absolute-position blocks at the same y. Re-runs on structure
  // change; a `ResizeObserver` catches width changes (window
  // resizes, split-pane drags) so blocks reflow with the text.
  useEffect(() => {
    // Structure changed — any cached measurements are stale.
    measurementCacheRef.current.clear()
    // Reset the breaker so a stale trip from the previous structure
    // can't hold the fresh one frozen.
    circuitRef.current = { windowStart: 0, count: 0, cooldownUntil: 0 }
    const container = textContainerRef.current
    if (!container) return
    let rafId = 0
    let settleTimer: ReturnType<typeof setTimeout> | undefined
    const measure = () => {
      const now = performance.now()
      const cb = circuitRef.current
      // Held under cooldown? Skip entirely — will retry on the next
      // ResizeObserver tick after the cooldown expires.
      if (now < cb.cooldownUntil) return
      // Rolling-window rate limit. Trips when measurements fire
      // faster than the layout can settle (usually a two-way width
      // ping-pong that beats the width cache). Once tripped we freeze
      // for `CB_COOLDOWN_MS`, which lets React commit the current
      // positions and lets the browser finish its layout pass.
      if (now - cb.windowStart > CB_WINDOW_MS) {
        cb.windowStart = now
        cb.count = 0
      }
      cb.count++
      if (cb.count > CB_MAX_PER_WINDOW) {
        cb.cooldownUntil = now + CB_COOLDOWN_MS
        return
      }
      const width = Math.round(container.getBoundingClientRect().width)
      const cached = measurementCacheRef.current.get(width)
      if (cached) {
        // Already measured at this exact width — skip the DOM walk
        // and just publish the cached result (state setter will
        // no-op if the map content is identical).
        setSentencePositions(prev => sameMap(prev, cached) ? prev : cached)
        return
      }
      const spans = container.querySelectorAll('[data-sentence-key]')
      const containerRect = container.getBoundingClientRect()
      const next = new Map<string, { top: number; lineHeight: number }>()
      spans.forEach((el) => {
        const key = el.getAttribute('data-sentence-key')
        if (!key) return
        const rects = (el as HTMLElement).getClientRects()
        if (rects.length === 0) return
        const first = rects[0]
        next.set(key, {
          top: Math.round(first.top - containerRect.top),
          lineHeight: Math.max(1, Math.round(first.height)),
        })
      })
      measurementCacheRef.current.set(width, next)
      setSentencePositions(prev => sameMap(prev, next) ? prev : next)
    }
    // Settle-debounce (50 ms after the last ResizeObserver tick)
    // then RAF so the DOM walk runs inside the browser's paint
    // rhythm. During an active drag this coalesces the flood of
    // observer ticks into one measurement per pause, and gives the
    // circuit breaker room to reset its window between drags.
    const schedule = () => {
      if (settleTimer !== undefined) clearTimeout(settleTimer)
      settleTimer = setTimeout(() => {
        cancelAnimationFrame(rafId)
        rafId = requestAnimationFrame(measure)
      }, 50)
    }
    schedule()
    const ro = new ResizeObserver(schedule)
    ro.observe(container)
    return () => {
      if (settleTimer !== undefined) clearTimeout(settleTimer)
      cancelAnimationFrame(rafId)
      ro.disconnect()
    }
  }, [question.answerobj?.answerstructure])

  const handleCopyToClipboard = () => {
    const text = structureToPlainText(question.answerobj?.answerstructure)
    if (!text) return
    copyToClipboard(text)
  }

  const replylimitLow = (question.replylimit as any)?.answerlimit <= 50

  const onAnswerStructureChange = (structure: TextStructure) => updateQuestion(question.questionsha,
    {
      answerobj: {
        ...(question.answerobj ?? {} as AnswerWebResult),
        answerstructure: structure,
        // Any edit invalidates the answer-level trace — sim isn't
        // recomputed on the client. `null` (not `-1`) so both the
        // TraceArc and the tab tick take the N/A branch: the arc's
        // `resolvedValue == null` check flips it into "click to
        // generate" mode, whereas an explicit `-1` would just
        // render "-1" and swallow the click. Cast is needed because
        // `AnswerWebResult.simint` is typed `number | undefined`.
        simint: null as any,
      } as AnswerWebResult
    });

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

      {/* Answer header */}
      <Box sx={{
        display: 'flex', alignItems: 'center',
        px: 0.75, py: 0.5,
        borderBottom: 1, borderColor: 'divider',
        bgcolor: 'action.hover',
        gap: 0.5, flexShrink: 0,
      }}>
        {/* "Answer" title + streaming / completion tick. Formatted
            the same weight/size/colour as the tab labels. */}
        <Typography sx={{ color: 'caption', fontWeight: 500, fontSize: FS_SM, px: 0.5, opacity: 0.6 }}>Answer</Typography>
        {isStreaming
          ? <CircularProgress size={ICON_XS} thickness={5} sx={{ mx: 0.25 }} />
          : hasAnswer
            ? <CheckCircleOutlineIcon sx={{ fontSize: ICON_XS, color: 'success.main' }} />
            : <RadioButtonUncheckedIcon sx={{ fontSize: ICON_XS, opacity: 0.3 }} />}

        {onToggleLock && (
          <TaPadlock isLocked={isLocked} onToggle={onToggleLock} />
        )}
        <Box sx={{ flex: 1, justifyContent: 'center', display: 'flex' }} >
          <Typography variant="caption" sx={{ opacity: 0.35, fontSize: FS_XS, ml: 0.5 }}>
            AI can make mistakes. Check important info
          </Typography>
        </Box>
        <TaWordCount
          structure={question.answerobj?.answerstructure}
          replylimit={question.replylimit}
          question={question}
          referral="pm"
          onWizardOpen={onOpenWordWizard}
        />
        <TraceArc
          value={question.answerobj?.simint}
          question={question}
          loading={tracing}
          tooltip={tracing ? 'Generating trace…' : 'Trace similarity'}
        />
        <ScoreArc
          value={question.bideval?.scoreint}
          question={question}
          loading={scoring}
          tooltip={scoring ? 'Estimating score…' : 'Estimated score'}
        />

        {headerEndSlot}
      </Box>

      {/* Toolbar — was the TabHeader. Left: answer tweaks. Right:
          trace toggle + editor buttons. */}
      <Box sx={{
        display: 'flex', alignItems: 'center',
        px: 0, py: 0.25,
        borderBottom: 1, borderColor: 'divider',
        gap: 0.25, flexShrink: 0,
        minHeight: 36,
      }}>
        <TaTraceToggle
          checked={showTrace}
          onChange={setShowTrace}
          testId="pmq_trace_toggle"
        />
        <TaElements
          mode="answer"
          question={question}
          disabled={isLocked || replylimitLow}
        />

        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <Tooltip title="Undo">
            <span>
              <IconButton
                size="small"
                data-testid="pmq_answer_undo_btn"
                disabled={!canUndo}
                onClick={() => taResponseRef.current?.undo()}
                sx={{ p: 0.375 }}
              >
                <UndoIcon sx={{ fontSize: ICON_XS }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Redo">
            <span>
              <IconButton
                size="small"
                data-testid="pmq_answer_redo_btn"
                disabled={!canRedo}
                onClick={() => taResponseRef.current?.redo()}
                sx={{ p: 0.375 }}
              >
                <RedoIcon sx={{ fontSize: ICON_XS }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Copy to Clipboard">
            <IconButton size="small" onClick={handleCopyToClipboard} sx={{ p: 0.375 }}>
              <ContentCopyIcon sx={{ fontSize: ICON_XS }} />
            </IconButton>
          </Tooltip>
          <OpenInAdvancedEditButton
            question={question}
            disabled={isLocked}
            testId="pmq_advedit_btn"
          />
          {threeDotsItems && threeDotsItems.length > 0 && (
            <TaThreeDots droplist={threeDotsItems} />
          )}
        </Box>
      </Box>

      {/* Answer content */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 0.75, py: 0.5, minHeight: 0 }}>
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'stretch', minHeight: 0 }}>
          {showTrace && (
            <TaResponseGutterColumn
              structure={question.answerobj?.answerstructure}
              linkId={`${editorId ?? 'pmq_answer'}_sources`}
              onSentenceHover={setHoveredSentence}
              sentencePositions={sentencePositions}
            />
          )}
          <Box ref={textContainerRef} sx={{ flex: 1, minWidth: 0 }}>
            {/* Plain BasicView editor — `paragraphHtml` embeds an
                  invisible `<span data-sentence-key>` around each
                  sentence. The gutter publishes the hovered sentence
                  key + colour and BasicView's CSS attribute selector
                  paints the matching span. Editing behaves exactly
                  as it did before the gutter existed. */}
            <TaResponse
              handleRef={taResponseRef}
              onHistoryChange={({ canUndo, canRedo }) => { setCanUndo(canUndo); setCanRedo(canRedo) }}
              structure={question.answerobj?.answerstructure}
              onChange={onAnswerStructureChange}
              editable={!isLocked && !isStreaming}
              placeholder="No answer yet"
              id={editorId ?? 'pmq_answer'}
              hoveredSentenceKey={hoveredSentence?.key ?? null}
              hoveredSentenceColor={hoveredSentence?.color ?? null}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
